import { Navigate, NavLink, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, useTenant } from '../lib/api';
import { useRole } from '../lib/role';
import { dotClass } from '../lib/status';
import Overview from './tenant/Overview';
import Phases from './tenant/Phases';
import Mailboxes from './tenant/Mailboxes';
import Logs from './tenant/Logs';
import DomainSwap from './tenant/DomainSwap';

/** Tenant detail body. Now rendered inside a modal panel by
 * TenantsView; the outer route shows the tenant list behind it. The
 * status header here is intentionally minimal — domain + tenant login
 * only. The progress bar inside Overview communicates status, so we
 * don't echo "phase: oauth" beside the title. */
export default function TenantDetail({ onClose }: { onClose?: () => void }) {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: t, isLoading } = useTenant(id);
  const { isAdmin } = useRole();

  if (isLoading || !t) return <div className="p-8 text-dim">loading…</div>;

  async function handleDelete() {
    if (!t) return;
    const confirmed = window.confirm(
      `Remove ${t.target_domain} (${t.ga_upn}) from the webapp?\n\n` +
      `This removes the tenant row, mailboxes, phase history, and secrets from the webapp's database.\n` +
      `It does NOT touch the Microsoft 365 tenant itself.`
    );
    if (!confirmed) return;
    try {
      await api.deleteTenant(t.id);
      qc.invalidateQueries({ queryKey: ['tenants'] });
      if (onClose) onClose(); else nav('/tenants');
    } catch (e: any) {
      alert(`Delete failed: ${e.message}`);
    }
  }

  async function handleStopRun() {
    if (!t) return;
    const confirmed = window.confirm(
      `Stop any in-flight provisioning for ${t.target_domain}?\n\n` +
      `This cancels queued + running jobs for this tenant and resets ` +
      `status to "failed" so you can re-run cleanly. It does NOT ` +
      `touch mailboxes, DNS, or the M365 tenant.`
    );
    if (!confirmed) return;
    try {
      const out = await api.stopTenantRun(t.id);
      qc.invalidateQueries({ queryKey: ['tenants'] });
      qc.invalidateQueries({ queryKey: ['tenant', t.id] });
      const d: any = out?.detail || {};
      const q = (d.cancelled_queued || []).length;
      const r = (d.stopped_running || []).length;
      const lock = d.lock_released ? 'yes' : 'no';
      alert(`Stopped. cancelled_queued=${q}, stopped_running=${r}, lock_released=${lock}. Tenant is now "failed" — use Re-run pipeline when ready.`);
    } catch (e: any) {
      alert(`Stop failed: ${e.message}`);
    }
  }

  async function handleTeardown() {
    if (!t) return;
    const first = window.confirm(
      `TEARDOWN ${t.target_domain}?\n\n` +
      `This will use the Microsoft Graph API to:\n` +
      `  • DELETE every mailbox user in the tenant\n` +
      `  • DELETE the anchor user\n` +
      `  • DELETE the custom domain ${t.target_domain}\n` +
      `  • Remove the tenant from the webapp\n\n` +
      `This is IRREVERSIBLE and affects the live Microsoft 365 tenant.`
    );
    if (!first) return;
    const typed = window.prompt(
      `Type the domain "${t.target_domain}" exactly to confirm teardown:`
    );
    if (typed !== t.target_domain) {
      if (typed !== null) alert('Domain did not match — teardown cancelled.');
      return;
    }
    try {
      await api.teardownTenant(t.id);
      qc.invalidateQueries({ queryKey: ['tenants'] });
      alert('Teardown job enqueued — watch the Logs tab until it completes, then the tenant will disappear from the list.');
    } catch (e: any) {
      alert(`Teardown failed to enqueue: ${e.message}`);
    }
  }

  const tab = (to: string, label: string) => (
    <NavLink to={to} end className={({ isActive }) =>
      `px-3 py-2 text-sm border-b-2 ${isActive ? 'border-run text-ink' : 'border-transparent text-dim hover:text-ink'}`}>
      {label}
    </NavLink>
  );

  return (
    <div className="flex flex-col h-full">
      <header className="border-b border-line px-6 py-4">
        <div className="flex items-center gap-3">
          <span className={dotClass(t.status)} />
          <h1 className="text-base font-semibold">{t.target_domain}</h1>
          <span className="text-xs text-dim">· {t.ga_upn}</span>
          {isAdmin && (
            <div className="ml-auto flex items-center gap-3">
              <button
                onClick={handleStopRun}
                className="text-xs text-warn hover:underline"
                title="Cancel any queued or in-flight provisioning job for this tenant and reset status to failed (so it can be re-run)"
              >
                Stop run
              </button>
              <button
                onClick={handleTeardown}
                className="text-xs text-err hover:underline"
                title="Delete mailboxes + custom domain from Microsoft 365, then remove from webapp"
              >
                Teardown (M365)
              </button>
              <button
                onClick={handleDelete}
                className="text-xs text-dim hover:underline"
                title="Remove this tenant from the webapp only (does not affect Microsoft 365)"
              >
                Remove from webapp
              </button>
            </div>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className={`${isAdmin ? '' : 'ml-auto'} text-dim hover:text-ink transition rounded-md p-1 -m-1`}
              aria-label="Close"
              title="Close (Esc)"
            >
              <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                <path d="M3 3 L13 13 M13 3 L3 13" />
              </svg>
            </button>
          )}
        </div>
      </header>
      <nav className="border-b border-line px-4 flex">
        {tab('.', 'Overview')}
        {isAdmin && tab('phases', 'Phases')}
        {tab('mailboxes', 'Mailboxes')}
        {isAdmin && tab('logs', 'Logs')}
        {tab('domain-swap', 'Domain swap')}
      </nav>
      <div className="flex-1 overflow-auto">
        <Routes>
          <Route index element={<Overview />} />
          {isAdmin && <Route path="phases" element={<Phases />} />}
          <Route path="mailboxes" element={<Mailboxes />} />
          {isAdmin && <Route path="logs" element={<Logs />} />}
          <Route path="domain-swap" element={<DomainSwap />} />
          {/* Operators hitting an admin-only path get bounced to overview. */}
          {!isAdmin && <Route path="*" element={<Navigate to="." replace />} />}
        </Routes>
      </div>
    </div>
  );
}
